import React, { useEffect, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
// SERVICES
// APP
import RemoveModal from "../../modals/removeModal";
// MATERIAL UI
import Box from "@mui/material/Box";
import Stack from "@mui/material/Stack";
import Typography from "@mui/material/Typography";
import IconButton from "@mui/material/IconButton";
import Button from "@mui/material/Button";
import Card from "@mui/material/Card";
import CardContent from "@mui/material/CardContent";
import Tooltip from "@mui/material/Tooltip";
import Alert from "@mui/material/Alert";
import Collapse from "@mui/material/Collapse";
import { alpha, useTheme } from "@mui/material/styles";
import Skeleton from "@mui/material/Skeleton";
import Avatar from "@mui/material/Avatar";
import Grid from "@mui/material/Grid";
import Divider from "@mui/material/Divider";
import Chip from "@mui/material/Chip";

// ICONS
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import AddCircleIcon from "@mui/icons-material/AddCircle";
import LocationOnIcon from "@mui/icons-material/LocationOn";
import MapIcon from "@mui/icons-material/Map";
import SignalCellularAltIcon from "@mui/icons-material/SignalCellularAlt";

import { dispatchSnackBarMessage } from "../../../utils/snackbar";
import {
  createNeighborhoodGroup,
  deleteNeighborhoodGroup,
  editNeighborhoodGroup,
  getNeighborhoodGroups,
} from "../../../api/neighborhoodGroups";
import NeighborhoodGroupFormModal from "./NeighborhoodGroupFormModal";
import { getTextColorByBackgroundContrast } from "../../../extras";

// Animation variants
const containerVariants = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: {
      staggerChildren: 0.06,
    },
  },
};

const itemVariants = {
  hidden: { y: 20, opacity: 0 },
  visible: {
    y: 0,
    opacity: 1,
    transition: {
      type: "spring",
      stiffness: 300,
      damping: 24,
    },
  },
  exit: {
    opacity: 0,
    scale: 0.96,
    transition: {
      duration: 0.2,
    },
  },
};

const buttonVariants = {
  hover: {
    scale: 1.05,
    boxShadow: "0px 5px 10px rgba(0, 0, 0, 0.1)",
  },
  tap: {
    scale: 0.95,
  },
};

// Radial gradient background for the color chip
const ColorAvatar = ({ color, label }) => {
  const textColor = getTextColorByBackgroundContrast(color);

  return (
    <motion.div
      whileHover={{ scale: 1.05 }}
      initial={{ scale: 0.95, opacity: 0 }}
      animate={{ scale: 1, opacity: 1 }}
      transition={{ duration: 0.3 }}
    >
      <Avatar
        sx={{
          backgroundColor: color,
          color: textColor,
          fontWeight: "bold",
          position: "relative",
          overflow: "visible",
          border: `2px solid ${alpha(color, 0.6)}`,
          boxShadow: `0 0 10px ${alpha(color, 0.4)}`,
          "&::before": {
            content: '""',
            position: "absolute",
            top: -2,
            left: -2,
            right: -2,
            bottom: -2,
            background: `radial-gradient(circle, ${alpha(
              color,
              0.4
            )} 0%, transparent 70%)`,
            zIndex: -1,
            borderRadius: "50%",
          },
        }}
      >
        {label.charAt(0).toUpperCase()}
      </Avatar>
    </motion.div>
  );
};

const NeighborhoodGroupList = () => {
  const theme = useTheme();
  const [loading, setLoading] = useState(false);
  const [neighborhoodGroups, setNeighborhoodGroups] = useState([]);
  const [editingNeighborhoodGroup, setEditingNeighborhoodGroup] =
    useState(null);
  const [
    showCreateOrEditNeighborhoodGroupModal,
    setShowCreateOrEditNeighborhoodGroupModal,
  ] = useState(false);

  const [neighborhoodGroupToDelete, setNeighborhoodGroupToDelete] =
    useState(null);
  const [
    showNeighborhoodGroupDeleteModal,
    setShowNeighborhoodGroupDeleteModal,
  ] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");
  const [showError, setShowError] = useState(false);

  useEffect(() => {
    setTimeout(async () => {
      await loadNeighborhoodGroups();
    }, 10);
  }, []);

  const loadNeighborhoodGroups = async () => {
    try {
      setLoading(true);
      const res = await getNeighborhoodGroups();
      setNeighborhoodGroups(res.data);
    } catch (e) {
      setErrorMessage(
        "Não foi possível carregar os bairros. Tente novamente mais tarde."
      );
      setShowError(true);
      dispatchSnackBarMessage(
        `neighborhood-group-list-${new Date()}`,
        "Erro ao carregar os bairros da sua matriz",
        "error"
      );
    } finally {
      setLoading(false);
    }
  };

  const handleCreateEditModal = () => {
    setShowCreateOrEditNeighborhoodGroupModal(
      !showCreateOrEditNeighborhoodGroupModal
    );
    setEditingNeighborhoodGroup(null);
  };

  const handleCreateNeighborhoodGroup = async (neighborhoodGroupObj) => {
    try {
      await createNeighborhoodGroup(neighborhoodGroupObj);
      handleCreateEditModal();
      await loadNeighborhoodGroups();
      dispatchSnackBarMessage(
        `create-neighborhood-group-success-${new Date()}`,
        "Bairro cadastrado com sucesso!",
        "success"
      );
    } catch (e) {
      const errorData = e.response?.data || {};
      let messages = "";
      for (const prop in errorData) {
        if (errorData.hasOwnProperty(prop)) {
          messages += errorData[prop].join("\n") + "\n";
        }
      }
      dispatchSnackBarMessage(
        `create-neighborhood-group-${new Date()}`,
        messages || "Erro ao cadastrar bairro",
        "error"
      );
    }
  };

  const handleEditNeighborhoodGroup = (neighborhoodGroup) => {
    setEditingNeighborhoodGroup(neighborhoodGroup);
    setShowCreateOrEditNeighborhoodGroupModal(
      !showCreateOrEditNeighborhoodGroupModal
    );
  };

  const confirmEditNeighborhoodGroup = async (neighborhoodGroupObj) => {
    try {
      await editNeighborhoodGroup(
        neighborhoodGroupObj,
        neighborhoodGroupObj.id
      );
      handleCreateEditModal();
      setEditingNeighborhoodGroup(null);
      await loadNeighborhoodGroups();
      dispatchSnackBarMessage(
        `edit-neighborhood-group-success-${new Date()}`,
        "Bairro atualizado com sucesso!",
        "success"
      );
    } catch (e) {
      const errorData = e.response?.data || {};
      let messages = "";
      for (const prop in errorData) {
        if (errorData.hasOwnProperty(prop)) {
          messages += errorData[prop].join("\n") + "\n";
        }
      }
      dispatchSnackBarMessage(
        `edit-neighborhood-group-${new Date()}`,
        messages || "Erro ao editar bairro",
        "error"
      );
    }
  };

  const handleDeleteNeighborhoodGroupModal = (neighborhoodGroup) => {
    setNeighborhoodGroupToDelete(neighborhoodGroup);
    setShowNeighborhoodGroupDeleteModal(!showNeighborhoodGroupDeleteModal);
  };

  const handleDeleteNeighborhoodGroup = async () => {
    try {
      await deleteNeighborhoodGroup(neighborhoodGroupToDelete.id);
      setShowNeighborhoodGroupDeleteModal(false);
      setNeighborhoodGroupToDelete(null);
      await loadNeighborhoodGroups();
      dispatchSnackBarMessage(
        `delete-neighborhood-group-success-${new Date()}`,
        "Bairro removido com sucesso!",
        "success"
      );
    } catch (error) {
      dispatchSnackBarMessage(
        `delete-neighborhood-group-error-${new Date()}`,
        "Erro ao remover bairro",
        "error"
      );
      setShowNeighborhoodGroupDeleteModal(false);
    }
  };

  if (loading) {
    return (
      <Box>
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
        >
          <Box sx={{ p: 3 }}>
            <Skeleton
              variant="rectangular"
              width="100%"
              height={50}
              sx={{ mb: 2, borderRadius: 2 }}
            />
            <Grid container spacing={2}>
              {[1, 2, 3, 4, 5, 6].map((item) => (
                <Grid item xs={12} sm={6} md={4} key={item}>
                  <Skeleton
                    variant="rectangular"
                    width="100%"
                    height={100}
                    sx={{ borderRadius: 2 }}
                  />
                </Grid>
              ))}
            </Grid>
          </Box>
        </motion.div>
      </Box>
    );
  }

  return (
    <>
      <Box sx={{ p: 2 }}>
        <Collapse in={showError} sx={{ mb: 2 }}>
          <Alert
            severity="error"
            onClose={() => setShowError(false)}
            sx={{ mb: 2 }}
          >
            {errorMessage}
          </Alert>
        </Collapse>

        <motion.div
          initial={{ opacity: 0, y: -10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.3 }}
        >
          <Box
            sx={{
              display: "flex",
              justifyContent: "center",
              mb: 3,
            }}
          >
            <motion.div
              variants={buttonVariants}
              whileHover="hover"
              whileTap="tap"
            >
              <Button
                variant="contained"
                startIcon={<AddCircleIcon />}
                onClick={handleCreateEditModal}
                color="primary"
                sx={{
                  borderRadius: 2,
                  px: 3,
                  py: 1,
                  fontWeight: "medium",
                  boxShadow: theme.shadows[3],
                  background: `linear-gradient(45deg, ${theme.palette.primary.main}, ${theme.palette.primary.light})`,
                }}
              >
                Cadastrar Novo Bairro
              </Button>
            </motion.div>
          </Box>
        </motion.div>

        <Box
          sx={{
            backgroundColor: alpha(theme.palette.background.paper, 0.6),
            borderRadius: 3,
            p: 2,
            boxShadow: theme.shadows[1],
            backdropFilter: "blur(8px)",
          }}
        >
          {neighborhoodGroups.length === 0 ? (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 0.2 }}
            >
              <Box
                sx={{
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "center",
                  justifyContent: "center",
                  py: 6,
                  px: 2,
                  backgroundColor: alpha(theme.palette.background.default, 0.5),
                  borderRadius: 2,
                  borderStyle: "dashed",
                  borderWidth: 1,
                  borderColor: theme.palette.divider,
                }}
              >
                <MapIcon
                  sx={{
                    fontSize: 48,
                    color: theme.palette.action.disabled,
                    mb: 2,
                  }}
                />
                <Typography variant="h6" color="textSecondary" gutterBottom>
                  Nenhum bairro cadastrado
                </Typography>
                <Typography
                  variant="body2"
                  color="textSecondary"
                  align="center"
                  sx={{ mb: 2, maxWidth: 400 }}
                >
                  Cadastre bairros para organizar suas entregas e definir taxas
                  específicas por região.
                </Typography>
                <motion.div
                  variants={buttonVariants}
                  whileHover="hover"
                  whileTap="tap"
                >
                  <Button
                    variant="outlined"
                    startIcon={<AddCircleIcon />}
                    onClick={handleCreateEditModal}
                    sx={{ mt: 1, borderRadius: 2 }}
                  >
                    Adicionar Bairro
                  </Button>
                </motion.div>
              </Box>
            </motion.div>
          ) : (
            <motion.div
              variants={containerVariants}
              initial="hidden"
              animate="visible"
            >
              <Grid container spacing={2}>
                <AnimatePresence>
                  {neighborhoodGroups.map((neighborhoodGroup) => (
                    <Grid item xs={12} sm={6} md={4} key={neighborhoodGroup.id}>
                      <motion.div variants={itemVariants} exit="exit" layout>
                        <Card
                          sx={{
                            borderRadius: 2,
                            height: "100%",
                            overflow: "visible",
                            boxShadow: theme.shadows[2],
                            transition: "all 0.3s ease",
                            position: "relative",
                            "&:hover": {
                              boxShadow: theme.shadows[5],
                              transform: "translateY(-4px)",
                            },
                            "&::before": {
                              content: '""',
                              position: "absolute",
                              top: 0,
                              left: 0,
                              width: "100%",
                              height: "5px",
                              backgroundColor: neighborhoodGroup.hex_color,
                              borderTopLeftRadius: 8,
                              borderTopRightRadius: 8,
                            },
                          }}
                        >
                          <CardContent>
                            <Box
                              sx={{
                                mb: 2,
                                display: "flex",
                                justifyContent: "space-between",
                                alignItems: "flex-start",
                              }}
                            >
                              <Box
                                sx={{ display: "flex", alignItems: "center" }}
                              >
                                <ColorAvatar
                                  color={neighborhoodGroup.hex_color}
                                  label={neighborhoodGroup.neighborhood}
                                />
                                <Box sx={{ ml: 2 }}>
                                  <Typography
                                    variant="h6"
                                    component="div"
                                    sx={{
                                      fontWeight: "bold",
                                      lineHeight: 1.2,
                                      mb: 0.5,
                                    }}
                                  >
                                    {neighborhoodGroup.neighborhood}
                                  </Typography>
                                  <Chip
                                    icon={
                                      <SignalCellularAltIcon fontSize="small" />
                                    }
                                    label={neighborhoodGroup.signal}
                                    size="small"
                                    sx={{
                                      backgroundColor: alpha(
                                        theme.palette.primary.main,
                                        0.1
                                      ),
                                      color: theme.palette.primary.main,
                                      fontWeight: "medium",
                                    }}
                                  />
                                </Box>
                              </Box>

                              <Box>
                                <Stack direction="row" spacing={1}>
                                  <Tooltip title="Editar bairro">
                                    <motion.div
                                      whileHover={{ scale: 1.1 }}
                                      whileTap={{ scale: 0.9 }}
                                    >
                                      <IconButton
                                        size="small"
                                        aria-label="edit"
                                        color="primary"
                                        onClick={() =>
                                          handleEditNeighborhoodGroup(
                                            neighborhoodGroup
                                          )
                                        }
                                        sx={{
                                          backgroundColor: alpha(
                                            theme.palette.primary.main,
                                            0.1
                                          ),
                                          "&:hover": {
                                            backgroundColor: alpha(
                                              theme.palette.primary.main,
                                              0.2
                                            ),
                                          },
                                        }}
                                      >
                                        <EditIcon fontSize="small" />
                                      </IconButton>
                                    </motion.div>
                                  </Tooltip>
                                  <Tooltip title="Excluir bairro">
                                    <motion.div
                                      whileHover={{ scale: 1.1 }}
                                      whileTap={{ scale: 0.9 }}
                                    >
                                      <IconButton
                                        size="small"
                                        aria-label="delete"
                                        color="error"
                                        onClick={() =>
                                          handleDeleteNeighborhoodGroupModal(
                                            neighborhoodGroup
                                          )
                                        }
                                        sx={{
                                          backgroundColor: alpha(
                                            theme.palette.error.main,
                                            0.1
                                          ),
                                          "&:hover": {
                                            backgroundColor: alpha(
                                              theme.palette.error.main,
                                              0.2
                                            ),
                                          },
                                        }}
                                      >
                                        <DeleteIcon fontSize="small" />
                                      </IconButton>
                                    </motion.div>
                                  </Tooltip>
                                </Stack>
                              </Box>
                            </Box>

                            <Divider sx={{ my: 1.5 }} />

                            <Box sx={{ mt: 1 }}>
                              <Box
                                sx={{
                                  display: "flex",
                                  alignItems: "center",
                                  mb: 1,
                                }}
                              >
                                <LocationOnIcon
                                  fontSize="small"
                                  sx={{
                                    color: theme.palette.text.secondary,
                                    mr: 1,
                                  }}
                                />
                                <Typography
                                  variant="body2"
                                  color="textSecondary"
                                >
                                  Localização
                                </Typography>
                              </Box>

                              <Typography
                                variant="body2"
                                sx={{
                                  color: alpha(theme.palette.text.primary, 0.8),
                                  fontStyle: "italic",
                                }}
                              >
                                Zona{" "}
                                {neighborhoodGroup.zone || "Não especificada"}
                              </Typography>
                            </Box>
                          </CardContent>
                        </Card>
                      </motion.div>
                    </Grid>
                  ))}
                </AnimatePresence>
              </Grid>
            </motion.div>
          )}
        </Box>
      </Box>

      <RemoveModal
        title="Excluir bairro"
        message={`Tem certeza que deseja excluir o bairro "${neighborhoodGroupToDelete?.neighborhood}" (${neighborhoodGroupToDelete?.signal})?`}
        show={showNeighborhoodGroupDeleteModal}
        toggleModal={handleDeleteNeighborhoodGroupModal}
        onRemove={handleDeleteNeighborhoodGroup}
      />

      <NeighborhoodGroupFormModal
        show={showCreateOrEditNeighborhoodGroupModal}
        group={editingNeighborhoodGroup}
        closeModal={handleCreateEditModal}
        onSave={
          editingNeighborhoodGroup
            ? confirmEditNeighborhoodGroup
            : handleCreateNeighborhoodGroup
        }
      />
    </>
  );
};

export default NeighborhoodGroupList;
